import React, { useEffect, useState } from "react";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { useFetchQrScanTimelineDetailMutation } from "../../../features/tenant/qrScanTimeline/qrScanTimelineApi";
import { toast } from "react-hot-toast";
import {
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineHashtag,
  HiOutlineInformationCircle,
  HiOutlineCurrencyRupee,
  HiOutlineChip,
  HiOutlineGift,
  HiOutlineLocationMarker,
  HiOutlineDeviceMobile,
  HiOutlineStar,
  HiOutlineRefresh,
  HiOutlineBadgeCheck,
  HiOutlineExclamationCircle,
  HiOutlineArrowRight,
  HiOutlineCube,
} from "react-icons/hi";

// ─── Status / type lookup maps ─────────────────────────────────────────────────
// Maps the numeric status code from qr_scan_hit.status to Tailwind colour tokens.
const STATUS_STYLE = {
  "1":  { bg: "bg-emerald-50",  text: "text-emerald-700",  border: "border-emerald-200",  dot: "bg-emerald-400" },
  "2":  { bg: "bg-amber-50",    text: "text-amber-700",    border: "border-amber-200",    dot: "bg-amber-400"   },
  "0":  { bg: "bg-red-50",      text: "text-red-700",      border: "border-red-200",      dot: "bg-red-400"     },
  "-1": { bg: "bg-slate-50",    text: "text-slate-600",    border: "border-slate-200",    dot: "bg-slate-400"   },
  "3":  { bg: "bg-sky-50",      text: "text-sky-700",      border: "border-sky-200",      dot: "bg-sky-400"     },
};

// Scan type: main = only base points, scheme = scheme-specific points, both = mixed
const TYPE_META = {
  main:   { bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",   icon: HiOutlineChip,  label: "Main Scan"   },
  scheme: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", icon: HiOutlineGift,  label: "Scheme Scan" },
  both:   { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", icon: HiOutlineStar,  label: "Both"        },
};

// ─── Shared micro-components ───────────────────────────────────────────────────

/** Pill badge rendered from a STATUS_STYLE entry — shows coloured dot + label */
const StatusPill = ({ label, statusKey }) => {
  const s = STATUS_STYLE[String(statusKey)] || STATUS_STYLE["0"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border ${s.bg} ${s.text} ${s.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {label}
    </span>
  );
};

/** Pill badge for scan type with matching icon */
const TypePill = ({ type }) => {
  const m = TYPE_META[type] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200", icon: HiOutlineInformationCircle, label: type };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border ${m.bg} ${m.text} ${m.border}`}>
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
};

/** Single metric card shown in the hero summary row */
const MetricCard = ({ icon: Icon, label, primary, secondary, accent = "blue" }) => {
  const accentMap = {
    blue:   "from-blue-50 to-white border-blue-100 text-blue-600",
    violet: "from-violet-50 to-white border-violet-100 text-violet-600",
    emerald:"from-emerald-50 to-white border-emerald-100 text-emerald-600",
    amber:  "from-amber-50 to-white border-amber-100 text-amber-600",
  };
  return (
    <div className={`bg-gradient-to-b ${accentMap[accent]} border rounded-2xl p-4 flex flex-col gap-1`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-white border ${accentMap[accent].split(" ")[3]} border-opacity-50 shadow-sm`}>
          <Icon className={`w-4 h-4 ${accentMap[accent].split(" ")[4]}`} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</span>
      </div>
      <p className="text-gray-900 font-black text-base leading-tight">{primary ?? "—"}</p>
      {secondary && <p className="text-gray-400 text-xs">{secondary}</p>}
    </div>
  );
};

/** Section header with a gradient left-border accent and optional counter chip */
const SectionHeading = ({ title, subtitle, count, accentColor = "blue" }) => {
  const borderMap = { blue: "border-blue-500", violet: "border-violet-500", emerald: "border-emerald-500", amber: "border-amber-500" };
  return (
    <div className={`flex items-center justify-between border-l-4 ${borderMap[accentColor]} pl-4 py-1 mb-4`}>
      <div>
        <h3 className="text-sm font-black text-gray-800">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {count !== undefined && (
        <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
          {count} {count === 1 ? "item" : "items"}
        </span>
      )}
    </div>
  );
};

/** Inline empty state for tables/sections that returned no rows */
const EmptyRows = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-300">
    <HiOutlineExclamationCircle className="w-8 h-8" />
    <p className="text-xs font-medium text-gray-400">{message}</p>
  </div>
);

/** Monospaced code-style chip — used for QR IDs and batch codes */
const CodeChip = ({ value }) => (
  <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md border border-gray-200">
    {value}
  </span>
);

// ─── Main modal component ──────────────────────────────────────────────────────
const QrScanTimelineDetailModal = ({ show, setShow, hitId }) => {
  const [fetchDetail, { isLoading }] = useFetchQrScanTimelineDetailMutation();
  const [detail, setDetail] = useState(null);

  // Reset state whenever a new hitId is opened so we never flash stale data.
  useEffect(() => {
    if (show && hitId) {
      setDetail(null);
      loadDetail();
    }
  }, [show, hitId]);

  const loadDetail = async () => {
    try {
      const response = await fetchDetail({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        hitId,
      }).unwrap();

      if (response.status) {
        setDetail(response.body);
      } else {
        toast.error(response.message || "Failed to fetch timeline detail");
      }
    } catch (err) {
      console.error("Timeline detail error:", err);
      toast.error("Something went wrong fetching timeline detail");
    }
  };

  const hit = detail?.hit;
  const qrTimeline = detail?.qr_timeline || [];
  const ruleRewards = detail?.rule_rewards || [];

  // Aggregate totals shown in the hero — summed across all QR IDs in this hit
  const totalMainPts = qrTimeline.reduce(
    (acc, qr) => acc + (qr.main_entries || []).reduce((s, e) => s + (Number(e.points) || 0), 0),
    0,
  );
  const totalSchemePts = qrTimeline.reduce(
    (acc, qr) => acc + (qr.scheme_entries || []).reduce((s, e) => s + (Number(e.points) || 0), 0),
    0,
  );
  const totalRuleReward = ruleRewards.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);

  return (
    <ModalComponent show={show} setShow={setShow} size="6xl" title="QR Scan Timeline">
      <div className="bg-gray-50/60 min-h-[300px]">

        {/* ── Loading state ─────────────────────────────────────────────── */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="relative">
              <div className="w-14 h-14 rounded-full border-4 border-gray-200" />
              <div className="w-14 h-14 rounded-full border-4 border-blue-500 border-t-transparent animate-spin absolute inset-0" />
            </div>
            <p className="text-sm font-semibold text-gray-500 tracking-wide">Building timeline…</p>
          </div>
        )}

        {/* ── Error / not loaded state ───────────────────────────────────── */}
        {!isLoading && !detail && (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-400">
            <HiOutlineExclamationCircle className="w-14 h-14 opacity-20" />
            <p className="text-base font-semibold text-gray-500">Could not load timeline</p>
            <button
              onClick={loadDetail}
              className="flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors"
            >
              <HiOutlineRefresh className="w-4 h-4" />
              Retry
            </button>
          </div>
        )}

        {/* ── Main content ───────────────────────────────────────────────── */}
        {!isLoading && detail && (
          <div className="divide-y divide-gray-200">

            {/* ── Hero: scan-level overview ─────────────────────────────── */}
            <div className="bg-white px-6 pt-5 pb-6 space-y-5">

              {/* User identity + quick badges */}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  {/* Avatar circle using user initials */}
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-lg shadow-md shadow-blue-200 flex-shrink-0">
                    {(hit.user_name || "U")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-black text-gray-900 text-base leading-tight">{hit.user_name || "Unknown User"}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <HiOutlineDeviceMobile className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">{hit.user_mobile || "—"}</span>
                      {(hit.city || hit.state) && (
                        <>
                          <span className="text-gray-300">·</span>
                          <HiOutlineLocationMarker className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {[hit.city, hit.state].filter(Boolean).join(", ")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Status + type pills */}
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill label={hit.status_label} statusKey={hit.status} />
                  <TypePill type={hit.type} />
                  {hit.platform && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-100 text-gray-500 border border-gray-200">
                      {hit.platform}
                    </span>
                  )}
                </div>
              </div>

              {/* Key metric cards — 4 up on desktop, 2×2 on mobile */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricCard
                  icon={HiOutlineCalendar}
                  label="Scanned At"
                  primary={new Date(hit.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  secondary={new Date(hit.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  accent="blue"
                />
                <MetricCard
                  icon={HiOutlineHashtag}
                  label="QRs Scanned"
                  primary={hit.count}
                  secondary={`${qrTimeline.length} resolved`}
                  accent="violet"
                />
                <MetricCard
                  icon={HiOutlineStar}
                  label="Total Points"
                  primary={`+${totalMainPts + totalSchemePts}`}
                  secondary={`${totalMainPts} main · ${totalSchemePts} scheme`}
                  accent="emerald"
                />
                <MetricCard
                  icon={HiOutlineGift}
                  label="Rule Rewards"
                  primary={ruleRewards.length > 0 ? `+${totalRuleReward}` : "None"}
                  secondary={`${ruleRewards.length} rule${ruleRewards.length !== 1 ? "s" : ""} triggered`}
                  accent="amber"
                />
              </div>
            </div>

            {/* ── QR-level timeline ─────────────────────────────────────── */}
            <div className="px-6 py-6 space-y-3">
              <SectionHeading
                title="QR Code Breakdown"
                subtitle="Each QR code scanned in this batch, with its point entries"
                count={qrTimeline.length}
                accentColor="blue"
              />

              {qrTimeline.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <EmptyRows message="No QR data resolved for this scan" />
                </div>
              ) : (
                /* Vertical timeline — each QR gets a card anchored by a numbered marker */
                <div className="relative space-y-4">
                  {/* Connecting vertical line behind the number markers */}
                  {qrTimeline.length > 1 && (
                    <div className="absolute left-[19px] top-8 bottom-8 w-px bg-gray-200 z-0" />
                  )}

                  {qrTimeline.map((qr, idx) => (
                    <QrTimelineCard key={qr.qr_id} qr={qr} index={idx + 1} total={qrTimeline.length} />
                  ))}
                </div>
              )}
            </div>

            {/* ── Loyalty rules rewards ─────────────────────────────────── */}
            {/* Only rendered when at least one rule fired for this scan hit */}
            {ruleRewards.length > 0 && (
              <div className="px-6 py-6 space-y-3">
                <SectionHeading
                  title="Loyalty Rule Rewards"
                  subtitle="Rules that triggered and credited bonus points for this scan"
                  count={ruleRewards.length}
                  accentColor="amber"
                />

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-amber-50/60 border-b border-amber-100">
                          {["Rule", "Reward Type", "Amount", "Description", "Triggered At"].map((h) => (
                            <th key={h} className="px-5 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {ruleRewards.map((r, i) => (
                          <tr key={r.id} className={`transition-colors hover:bg-amber-50/30 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                            <td className="px-5 py-3.5">
                              <CodeChip value={`#${r.rule_id}`} />
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wide">
                                <HiOutlineBadgeCheck className="w-3 h-3" />
                                {r.transaction_type}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="text-sm font-black text-emerald-600">+{r.amount}</span>
                            </td>
                            <td className="px-5 py-3.5 text-sm text-gray-500 max-w-[220px]">
                              <span className="block truncate" title={r.description}>{r.description || "—"}</span>
                            </td>
                            <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                              {new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {/* Totals footer row */}
                      <tfoot>
                        <tr className="bg-amber-50/40 border-t-2 border-amber-100">
                          <td colSpan={2} className="px-5 py-3 text-xs font-black text-gray-500 uppercase tracking-widest">
                            Total Rule Reward
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-base font-black text-emerald-600">+{totalRuleReward}</span>
                          </td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Footer actions ────────────────────────────────────────── */}
            <div className="bg-white px-6 py-4 flex items-center justify-between">
              <p className="text-xs text-gray-400">Hit ID <CodeChip value={`#${hit.id}`} /></p>
              <button
                onClick={() => setShow(false)}
                className="px-6 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-md shadow-blue-200 active:scale-95"
              >
                Close
              </button>
            </div>

          </div>
        )}
      </div>
    </ModalComponent>
  );
};

// ─── QrTimelineCard ────────────────────────────────────────────────────────────
/**
 * Renders a single QR code's portion of the timeline:
 *   - Numbered position marker + product info header
 *   - Main scan entries table (user_point_entries) — shown when type is 'main' or 'both'
 *   - Scheme entries table (scheme_point_entries)  — shown when type is 'scheme' or 'both'
 */
const QrTimelineCard = ({ qr, index, total }) => {
  const hasMain   = qr.main_entries?.length > 0;
  const hasScheme = qr.scheme_entries?.length > 0;

  const totalMainPts   = (qr.main_entries   || []).reduce((s, e) => s + (Number(e.points) || 0), 0);
  const totalSchemePts = (qr.scheme_entries || []).reduce((s, e) => s + (Number(e.points) || 0), 0);
  const grandTotal     = totalMainPts + totalSchemePts;

  return (
    <div className="relative flex gap-4 z-10">

      {/* ── Timeline position marker ─────────────────────────────────── */}
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border-2 border-blue-200 shadow-sm flex items-center justify-center z-10">
        <span className="text-xs font-black text-blue-600">{index}</span>
      </div>

      {/* ── QR card body ─────────────────────────────────────────────── */}
      <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* Card header: QR ID, product, batch, MRP, total points */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          {/* QR identifier */}
          <div className="flex items-center gap-2">
            <HiOutlineCube className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">QR ID</p>
              <CodeChip value={`#${qr.qr_id}`} />
            </div>
          </div>

          {/* Product details */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Product</p>
            <p className="text-sm font-bold text-gray-800 leading-tight">{qr.product_name || "—"}</p>
            {qr.product_code && (
              <p className="text-[11px] text-gray-400 mt-0.5">{qr.product_code}</p>
            )}
          </div>

          {/* Batch running code from generation table */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Batch Code</p>
            <CodeChip value={qr.batch_running_code || "—"} />
          </div>

          {/* MRP — optional, only present when generation has price info */}
          {qr.qr_mrp != null && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">MRP</p>
              <span className="flex items-center gap-0.5 text-sm font-bold text-gray-700">
                <HiOutlineCurrencyRupee className="w-3.5 h-3.5" />
                {qr.qr_mrp}
              </span>
            </div>
          )}

          {/* Points earned pill — pushed to the right */}
          <div className="ml-auto flex items-center gap-3">
            {(qr.scan_type === "main" || qr.scan_type === "both") && (
              <div className="text-center">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Main</p>
                <p className={`text-base font-black ${totalMainPts > 0 ? "text-blue-600" : "text-gray-300"}`}>
                  {totalMainPts > 0 ? `+${totalMainPts}` : "—"}
                </p>
              </div>
            )}
            {(qr.scan_type === "scheme" || qr.scan_type === "both") && (
              <div className="text-center">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Scheme</p>
                <p className={`text-base font-black ${totalSchemePts > 0 ? "text-violet-600" : "text-gray-300"}`}>
                  {totalSchemePts > 0 ? `+${totalSchemePts}` : "—"}
                </p>
              </div>
            )}
            {grandTotal > 0 && (
              <>
                <div className="w-px h-8 bg-gray-200" />
                <div className="text-center">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Total</p>
                  <p className="text-base font-black text-emerald-600">+{grandTotal}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Main scan entries (user_point_entries) ──────────────────── */}
        {/* Only rendered when the hit type includes 'main' scans */}
        {(qr.scan_type === "main" || qr.scan_type === "both") && (
          <div className="border-b border-gray-100 last:border-b-0">
            {/* Sub-section label */}
            <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-50/40 border-b border-blue-100">
              <HiOutlineChip className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                Base Point Entries
              </span>
              <HiOutlineArrowRight className="w-3 h-3 text-blue-300" />
              <span className="text-[10px] text-blue-400">user_point_entries</span>
              <span className="ml-auto text-[10px] text-blue-400 font-semibold">
                {qr.main_entries?.length || 0} row{qr.main_entries?.length !== 1 ? "s" : ""}
              </span>
            </div>

            {hasMain ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-white border-b border-gray-50">
                      {["Points", "Amount (₹)", "Method", "Status", "Credited At"].map((h) => (
                        <th key={h} className="px-5 py-2.5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {qr.main_entries.map((e, i) => (
                      <tr key={e.id} className={`transition-colors hover:bg-blue-50/20 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/20"}`}>
                        <td className="px-5 py-3">
                          <span className={`text-sm font-black ${e.points > 0 ? "text-emerald-600" : e.points < 0 ? "text-red-500" : "text-gray-400"}`}>
                            {e.points > 0 ? `+${e.points}` : e.points}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-600 font-medium">
                          {e.amount != null ? e.amount : "—"}
                        </td>
                        <td className="px-5 py-3">
                          {e.method ? (
                            <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md border border-gray-200 uppercase">
                              {e.method}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md border border-gray-200">
                            {e.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Mini-footer with the main-point sub-total */}
                  {totalMainPts > 0 && (
                    <tfoot>
                      <tr className="bg-blue-50/30 border-t border-blue-100">
                        <td className="px-5 py-2.5">
                          <span className="text-sm font-black text-blue-700">+{totalMainPts} pts</span>
                        </td>
                        <td colSpan={4} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            ) : (
              <EmptyRows message="No base point entries found for this QR" />
            )}
          </div>
        )}

        {/* ── Scheme entries (scheme_point_entries) ───────────────────── */}
        {/* Only rendered when the hit type includes 'scheme' scans */}
        {(qr.scan_type === "scheme" || qr.scan_type === "both") && (
          <div>
            {/* Sub-section label */}
            <div className="flex items-center gap-2 px-5 py-2.5 bg-violet-50/40 border-b border-violet-100">
              <HiOutlineGift className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-[10px] font-black text-violet-600 uppercase tracking-widest">
                Scheme Point Entries
              </span>
              <HiOutlineArrowRight className="w-3 h-3 text-violet-300" />
              <span className="text-[10px] text-violet-400">scheme_point_entries</span>
              <span className="ml-auto text-[10px] text-violet-400 font-semibold">
                {qr.scheme_entries?.length || 0} row{qr.scheme_entries?.length !== 1 ? "s" : ""}
              </span>
            </div>

            {hasScheme ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-white border-b border-gray-50">
                      {["Scheme", "Points", "Remark", "Status", "Reverted", "Credited At"].map((h) => (
                        <th key={h} className="px-5 py-2.5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {qr.scheme_entries.map((e, i) => (
                      <tr
                        key={e.id}
                        className={`transition-colors hover:bg-violet-50/20 ${e.is_reverted ? "opacity-50" : ""} ${i % 2 === 0 ? "bg-white" : "bg-gray-50/20"}`}
                      >
                        <td className="px-5 py-3 max-w-[160px]">
                          <p className="text-sm font-semibold text-gray-800 truncate" title={e.scheme_name}>
                            {e.scheme_name || `Scheme #${e.scheme_id}`}
                          </p>
                          <p className="text-[10px] text-gray-400">ID: {e.scheme_id}</p>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-sm font-black ${e.points > 0 ? "text-violet-600" : e.points < 0 ? "text-red-500" : "text-gray-400"}`}>
                            {e.points > 0 ? `+${e.points}` : e.points}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500 max-w-[150px]">
                          <span className="block truncate" title={e.remark}>{e.remark || "—"}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md border border-gray-200">
                            {e.status}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {e.is_reverted ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                              <HiOutlineRefresh className="w-3 h-3" />
                              Reverted
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Scheme sub-total footer */}
                  {totalSchemePts > 0 && (
                    <tfoot>
                      <tr className="bg-violet-50/30 border-t border-violet-100">
                        <td className="px-5 py-2.5">
                          <span className="text-sm font-black text-violet-700">+{totalSchemePts} pts</span>
                        </td>
                        <td colSpan={5} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            ) : (
              <EmptyRows message="No scheme entries found for this QR" />
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default QrScanTimelineDetailModal;
